import * as React from "react";
import { css } from "@emotion/core";
import { useVirtualizer } from "@tanstack/react-virtual";
import { formatBytes } from "utils";
import {
  Button,
  ButtonGroup,
  Card,
  Menu,
  MenuItem,
  Popover,
} from "@blueprintjs/core";
import { ProcessedImage } from "components/Orders/state/types";
import { useUploadMachineSelector } from "../../context/upload-machine";
import { FileUploadActor } from "components/Orders/state/uploadMachine/types";
import { ImagesMachineContext } from "components/Orders/state/imagesMachine";
import { OrderMachineContext } from "components/Orders/state/orderMachine";
import { formatCaptureTimeForDisplay } from "components/Orders/utils/validation";
import { ConfirmRemoveDialog } from "./ConfirmRemoveDialog";
import { generateUniqueFileIdentifier } from "components/Orders/state/uploadMachine";
import { virtualizerStore } from "../../state/virtualizerStore";
import { Icon } from "@blueprintjs/core";
import { useImageDropzone } from "../../hooks";
import { UploadStatusIcon } from "./UploadStatusIcon";
import MapView from "./MapView";

function ImagesConfirmRemoveDialog() {
  const imagesMachine = ImagesMachineContext.useActorRef();

  const show = ImagesMachineContext.useSelector((state) =>
    state.matches("remove")
  );

  const image = ImagesMachineContext.useSelector((state) =>
    state.context.uploads.find((u) => u.id === state.context.removing)
  );

  const timezone = OrderMachineContext.useSelector((s) => s.context.timezone);

  const captured = image ? formatCaptureTimeForDisplay(image, timezone) : null;

  const [preview, setPreview] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!show || !image) {
      setPreview(null);
      return;
    }

    if (image.file instanceof File) {
      const objectUrl = URL.createObjectURL(image.file);
      setPreview(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [show, image]);

  const handleCancel = () => {
    imagesMachine.send({ type: "CANCEL_REMOVE" });
  };

  const handleConfirm = () => {
    imagesMachine.send({ type: "CONFIRM_REMOVE" });
  };

  if (show === false || image === undefined) return null;

  return (
    <ConfirmRemoveDialog onCancel={handleCancel} onConfirm={handleConfirm}>
      <Card
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <span>{image?.file.name}</span>
      </Card>
      <Card>
        <div
          css={css`
            padding: 1rem 0;
          `}
        >
          <img
            src={preview}
            css={css`
              width: 100%;
              border: 4px solid var(--neutral-1);
            `}
          />
        </div>
      </Card>
      <Card>
        <div
          css={css`
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <span>{captured?.date} </span>
            <span>{captured?.time}</span>
          </div>
          <span>{image ? formatBytes(image.file.size) : ""}</span>
        </div>
      </Card>
    </ConfirmRemoveDialog>
  );
}

function ImagesConfirmRemoveAllDialog() {
  const imagesMachine = ImagesMachineContext.useActorRef();

  const show = ImagesMachineContext.useSelector((state) =>
    state.matches({ clear: "confirm" })
  );

  const uploadCount = ImagesMachineContext.useSelector(
    (state) => state.context.uploads.length
  );

  const handleCancel = () => {
    imagesMachine.send({ type: "CANCEL_REMOVE" });
  };

  const handleConfirm = () => {
    imagesMachine.send({ type: "CONFIRM_REMOVE_ALL" });
  };

  if (show === false) return null;

  return (
    <ConfirmRemoveDialog onCancel={handleCancel} onConfirm={handleConfirm}>
      <Card
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <span>Remove all {uploadCount} images?</span>
      </Card>
    </ConfirmRemoveDialog>
  );
}

function ImagesConfirmRemoveSelectedDialog() {
  const imagesMachine = ImagesMachineContext.useActorRef();

  const show = ImagesMachineContext.useSelector((state) =>
    state.matches({ removeSelected: "confirm" })
  );

  const selectedCount = ImagesMachineContext.useSelector(
    (state) => state.context.selected.length
  );

  const handleCancel = () => {
    imagesMachine.send({ type: "CANCEL_REMOVE" });
  };

  const handleConfirm = () => {
    imagesMachine.send({ type: "CONFIRM_REMOVE_SELECTED" });
  };

  if (show === false) return null;

  return (
    <ConfirmRemoveDialog onCancel={handleCancel} onConfirm={handleConfirm}>
      <Card
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <span>Remove {selectedCount} selected images?</span>
      </Card>
    </ConfirmRemoveDialog>
  );
}

function ImagesConfirmRemoveMissingDialog() {
  const imagesMachine = ImagesMachineContext.useActorRef();

  const show = ImagesMachineContext.useSelector((state) =>
    state.matches({ removeMissing: "confirm" })
  );

  const missingCount = ImagesMachineContext.useSelector(
    (state) =>
      state.context.uploads.filter(
        (upload) =>
          !(upload.file instanceof File) &&
          (upload as any).status !== "uploaded"
      ).length
  );

  const handleCancel = () => {
    imagesMachine.send({ type: "CANCEL_REMOVE" });
  };

  const handleConfirm = () => {
    imagesMachine.send({ type: "CONFIRM_REMOVE_MISSING" });
  };

  if (show === false) return null;

  return (
    <ConfirmRemoveDialog onCancel={handleCancel} onConfirm={handleConfirm}>
      <Card
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        `}
      >
        <span>Remove {missingCount} missing images?</span>
      </Card>
    </ConfirmRemoveDialog>
  );
}

function ImageRow({
  image,
  onFocus,
  isOld,
}: {
  image: ProcessedImage;
  onFocus: () => void;
  isOld: boolean;
}) {
  const imagesMachine = ImagesMachineContext.useActorRef();

  const hasGeolocationIssue = React.useMemo(() => {
    return image.data && !image.data.exif?.geoLocation;
  }, [image.data]);

  const completed = useUploadMachineSelector((state) => {
    const fileIdentifier = generateUniqueFileIdentifier(image.file);
    return state.context.uploadedFiles.includes(fileIdentifier.name);
  });

  const active = useUploadMachineSelector((state) => {
    return state.context.fileUploadActors.some((actor: FileUploadActor) => {
      const fileIdentifier = generateUniqueFileIdentifier(image.file);
      return actor.id === `file-${fileIdentifier.name}`;
    });
  });

  const idle = ImagesMachineContext.useSelector((state) =>
    state.matches("idle")
  );

  const uploadInProgress = useUploadMachineSelector((state) => {
    return state.context.fileUploadActors.length > 0;
  });

  const hover = ImagesMachineContext.useSelector(
    (state) => state.context.hover[image.id]
  );

  const selected = ImagesMachineContext.useSelector((state) =>
    state.context.selected.includes(image.id)
  );

  const handleClick = () => {
    if (uploadInProgress) {
      return;
    }

    if (selected) {
      imagesMachine.send({ type: "MARKER_DESELECT", id: image.id });
    } else {
      imagesMachine.send({ type: "MARKER_SELECT", id: image.id });
    }

    onFocus();
  };

  const handleRemove = () => {
    imagesMachine.send({ type: "REMOVE_FILE", id: image.id });
  };

  return (
    <div
      css={css`
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.25rem;
      `}
    >
      <div
        css={css`
          border: 1px solid var(--neutral-6);
        `}
      >
        <UploadStatusIcon
          completed={completed}
          active={active}
          uploadInProgress={uploadInProgress}
          fileAvailable={image.file instanceof File}
          isOld={isOld}
          type="image"
        />
      </div>
      <div
        css={css`
          flex-grow: 1;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          padding: 0 0.25rem 0 0.5rem;
          height: 40px;
          border: 1px solid
            ${selected
              ? hover
                ? "var(--fybr-blue)"
                : "var(--fybr-blue-translucent)"
              : hover
              ? "var(--neutral-4)"
              : "var(--neutral-5)"};
          background-color: ${selected
            ? hover
              ? "var(--fybr-blue)"
              : "var(--fybr-blue-translucent)"
            : hover
            ? "var(--neutral-6)"
            : "var(--neutral-6-translucent)"};
        `}
        onMouseOver={() =>
          imagesMachine.send({ type: "MARKER_OVER", id: image.id })
        }
        onMouseOut={() =>
          imagesMachine.send({ type: "MARKER_OUT", id: image.id })
        }
        onClick={handleClick}
      >
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 1rem;
          `}
        >
          <div
            css={css`
              width: 100%;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              display: flex;
              align-items: center;
              gap: 0.5rem;
            `}
          >
            <span>{image.file.name}</span>
            {hasGeolocationIssue && (
              <Icon
                icon="warning-sign"
                intent="warning"
                title="No GPS location data found in this image"
              />
            )}
          </div>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 1rem;
            flex-shrink: 0;
          `}
        >
          <span>{formatBytes(image.file.size)}</span>
          <Button
            icon="trash"
            intent="danger"
            disabled={idle === false || uploadInProgress}
            onClick={(event) => {
              event.stopPropagation();
              handleRemove();
            }}
          />
        </div>
      </div>
    </div>
  );
}

function ReaddMissingImagesButton() {
  const imagesMachine = ImagesMachineContext.useActorRef();
  const imagesDropzone = useImageDropzone(imagesMachine);

  return (
    <Button
      icon="media"
      intent="success"
      size="small"
      {...imagesDropzone.getRootProps()}
    >
      <input {...imagesDropzone.getInputProps()} />
      Re-add Missing Images
    </Button>
  );
}

function RemoveMissingImagesButton() {
  const imagesMachine = ImagesMachineContext.useActorRef();

  const uploadInProgress = useUploadMachineSelector((state) => {
    return state.context.fileUploadActors.length > 0;
  });

  const handleClick = () => {
    imagesMachine.send({ type: "REMOVE_MISSING" });
  };

  return (
    <Button
      icon="trash"
      intent="danger"
      size="small"
      disabled={uploadInProgress}
      onClick={handleClick}
    >
      Remove Missing Images
    </Button>
  );
}

interface ImagesListProps {
  images: ProcessedImage[];
  onFocus?: () => void;
  onSetLocationToCenter?: () => void;
  uploadInProgress: boolean;
  mapRef?: React.RefObject<mapboxgl.Map>;
  mapCenter?: [number, number];
  zoom?: number;
  onZoomEnd?: (event: any) => void;
  fileAgeMap: Map<string, boolean>;
}

export default function ImagesList(props: ImagesListProps) {
  const imagesMachine = ImagesMachineContext.useActorRef();

  const selected = ImagesMachineContext.useSelector(
    (state) => state.context.selected.length > 0
  );

  const uploadInProgress = useUploadMachineSelector((state) => {
    return state.context.fileUploadActors.length > 0;
  });

  const [viewMode, setViewMode] = React.useState<"map" | "list">("map");

  const missingImages = props.images.filter(
    (image) =>
      !(image.file instanceof File) && (image as any).status !== "uploaded"
  );

  const warningMissingImages = missingImages.length > 0;
  const hasMinimumImages = props.images.length >= 6;
  const errorMinimumImages =
    warningMissingImages === false &&
    props.images.length > 0 &&
    !hasMinimumImages;

  const sortedImages = React.useMemo(() => {
    return [...props.images].sort((a, b) => {
      const aIsMissing =
        !(a.file instanceof File) && (a as any).status !== "uploaded";
      const bIsMissing =
        !(b.file instanceof File) && (b as any).status !== "uploaded";
      const aIsUploaded = (a as any).status === "uploaded";
      const bIsUploaded = (b as any).status === "uploaded";

      if (aIsMissing && !bIsMissing) return -1;
      if (!aIsMissing && bIsMissing) return 1;

      if (aIsUploaded && !bIsUploaded) return 1;
      if (!aIsUploaded && bIsUploaded) return -1;

      return a.file.name.localeCompare(b.file.name);
    });
  }, [props.images]);

  const rowVirtualizer = useVirtualizer({
    count: sortedImages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 8,
  });

  const parentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    virtualizerStore.trigger.registerVirtualizer({
      virtualizer: rowVirtualizer,
    });
  }, [rowVirtualizer]);

  return (
    <div
      css={css`
        flex-grow: 1;
        width: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        border-top: 1px solid var(--neutral-5);
      `}
    >
      <div
        css={css`
          background-color: var(--neutral-6);
          border-bottom: 1px solid var(--neutral-5);
          padding: 0.5rem 1rem 0.5rem 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <h4
          css={css`
            color: var(--neutral-3);
            margin: 0;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 0.5rem;
          `}
        >
          <span>
            {props.images.length === 1
              ? "1 Image"
              : `${props.images.length} Images`}
          </span>
          {warningMissingImages ? (
            <span
              css={css`
                color: var(--danger);
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 0.25rem;
              `}
            >
              <Icon icon="error" color="var(--danger)" />
              <span>{missingImages.length} Missing</span>
            </span>
          ) : null}
        </h4>
        {warningMissingImages ? (
          <div
            css={css`
              flex-direction: row;
              align-items: center;
              gap: 0.5rem;
              display: flex;
            `}
          >
            <ButtonGroup>
              <ReaddMissingImagesButton />
              <RemoveMissingImagesButton />
            </ButtonGroup>
          </div>
        ) : props.uploadInProgress ? (
          <ButtonGroup>
            <Button
              intent={viewMode === "map" ? "primary" : undefined}
              icon="map"
              size="small"
              onClick={() => setViewMode("map")}
            >
              Map
            </Button>
            <Button
              intent={viewMode === "list" ? "primary" : undefined}
              icon="list"
              size="small"
              onClick={() => setViewMode("list")}
            >
              List
            </Button>
          </ButtonGroup>
        ) : errorMinimumImages ? (
          <div
            css={css`
              color: var(--danger);
              display: flex;
              flex-direction: row;
              align-items: center;
              gap: 0.25rem;
            `}
          >
            <Icon icon="error" color="var(--danger)" />
            <span>Need at least 6 images</span>
          </div>
        ) : (
          <Popover
            content={
              <Menu>
                <MenuItem
                  text="Set Location to Images Center"
                  icon="locate"
                  onClick={props.onSetLocationToCenter}
                />
                <MenuItem
                  text="Deselect All"
                  icon="cross"
                  disabled={selected === false}
                  onClick={() =>
                    imagesMachine.send({ type: "MARKER_DESELECT_ALL" })
                  }
                />
                <MenuItem text="Remove Images" icon="trash" intent="danger">
                  <MenuItem
                    text="Remove Selected"
                    intent="danger"
                    disabled={selected === false}
                    onClick={() =>
                      imagesMachine.send({ type: "REMOVE_SELECTED" })
                    }
                  />
                  <MenuItem
                    text="Remove All"
                    intent="danger"
                    onClick={() => imagesMachine.send({ type: "REMOVE_ALL" })}
                  />
                </MenuItem>
              </Menu>
            }
            placement="right"
          >
            <Button
              alignText="start"
              icon="media"
              endIcon="caret-down"
              text="Image Actions"
              intent={selected ? "primary" : undefined}
              disabled={uploadInProgress}
            />
          </Popover>
        )}
      </div>
      {props.uploadInProgress && viewMode === "map" ? (
        <MapView
          mapRef={props.mapRef}
          mapCenter={props.mapCenter}
          zoom={props.zoom}
          onZoomEnd={props.onZoomEnd}
        />
      ) : (
        <div
          ref={parentRef}
          css={css`
            flex-grow: 1;
            padding: 1rem;
            overflow: auto;
          `}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => (
              <div
                key={virtualRow.index}
                className={
                  virtualRow.index % 2 ? "ListItemOdd" : "ListItemEven"
                }
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <ImageRow
                  image={sortedImages[virtualRow.index]}
                  onFocus={props.onFocus}
                  isOld={props.fileAgeMap.get(sortedImages[virtualRow.index].id) ?? false}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <ImagesConfirmRemoveDialog />
      <ImagesConfirmRemoveAllDialog />
      <ImagesConfirmRemoveSelectedDialog />
      <ImagesConfirmRemoveMissingDialog />
    </div>
  );
}
